import { useMemo } from 'react';

import axios from 'axios';
import { useQuery } from 'react-query';

import processingActivitiesApi from 'config/api/processingActivities/processingActivities';
import type { AssetProcessingActivitiesMetrics } from 'config/api/processingActivities/processingActivities.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';

import {
  parseAssetProcessingActivitiesMetricsFromDetail,
  parseProcessingActivityDetailViewModel,
} from './processingActivityDetail.parsers';

type UseProcessingActivityDetailOptions = {
  organizationId?: number;
  processingActivityId?: number;
};

const useProcessingActivityDetail = ({ organizationId, processingActivityId }: UseProcessingActivityDetailOptions) => {
  const detailQuery = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_GET_DETAIL, organizationId, processingActivityId],
    async () => {
      const { data } = await processingActivitiesApi.getDetail(organizationId as number, processingActivityId as number);
      return data;
    },
    {
      enabled: Boolean(organizationId && processingActivityId),
    },
  );

  const assetProcessingsMetricsQuery = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_ASSET_PROCESSINGS_METRICS, organizationId, processingActivityId],
    async () => {
      const { data } = await processingActivitiesApi.getAssetProcessingActivitiesMetrics(
        organizationId as number,
        processingActivityId as number,
      );
      return data;
    },
    {
      enabled: Boolean(organizationId && processingActivityId),
      retry: false,
    },
  );

  const { data: detail, isError: isDetailError, isLoading, refetch, error: detailError } = detailQuery;
  const viewModel = useMemo(() => (detail ? parseProcessingActivityDetailViewModel(detail) : undefined), [detail]);

  const metricsFromDetail = detail ? parseAssetProcessingActivitiesMetricsFromDetail(detail) : null;
  const metricsFromEndpoint = assetProcessingsMetricsQuery.data;
  const metrics: AssetProcessingActivitiesMetrics | undefined = metricsFromEndpoint ?? metricsFromDetail ?? undefined;

  const isForbidden = Boolean(
    (isDetailError && axios.isAxiosError(detailError) && detailError.response?.status === 403) ||
      (isDetailError && axios.isAxiosError(detailError) && detailError.response?.status === 404),
  );

  const isError = isDetailError && !isForbidden;

  return {
    detail,
    isError,
    isForbidden,
    isLoading,
    metrics,
    refetch,
    viewModel,
  };
};

export default useProcessingActivityDetail;
